import { useEffect, useState } from 'react'
import axios from 'axios'

export const apiUrl = 'https://api.vibemap.com/v0.3'


// Get all lists
export const getAllLists = async () => {
    try {
        const cacheBuster = new Date().getTime()
        const endpoint = `${apiUrl}/lists/?_=${cacheBuster}`
        const response = await axios.get(endpoint)

        if (response.status !== 200) {
            throw new Error(`Error fetching lists: ${response.statusText}`)
        }
        if (!response.data) {
            throw new Error('No data received')
        }

        const data = response?.data?.results || []

        return data
    } catch (error) {
        console.error('ERROR: getAllLists', error)
        throw error
    }
}


// Get lists for a partner
export const getPartnerLists = async (email) => {
    try {
        const endpoint = `${apiUrl}/partners/`
        const response = await axios.get(endpoint)
        return response.data
    } catch (error) {
        console.error('ERROR: getPartnerLists', error)
        throw error
    }
}


// TODO: Get all lists for a user


// Get details for a specific partner
export const getPartnerDetails = async (slug) => {
    try {
        const endpoint = `${apiUrl}/partners/${slug}/`
        const response = await axios.get(endpoint)
        return response.data
    } catch (error) {
        console.error('ERROR: getPartnerDetails', error)
        return null
    }
}

// Create a new list
export const createList = async (listName, setAllLists, setMyLists) => {
    try {
        const endpoint = `${apiUrl}/lists/`
        const response = await axios.post(endpoint, { name: listName })
        const newList = response.data
        setAllLists((prevLists) => [...prevLists, newList])
        setMyLists((prevLists) => [...prevLists, newList])
        return newList
    } catch (err) {
        throw err
    }
}

export const saveList = async (listId, setAllLists, setMyLists) => {
    try {
        const endpoint = `${apiUrl}/lists/${listId}/`
        const response = await axios.patch(endpoint, { name: listId })
        const updatedList = response.data
        setAllLists((prevLists) =>
            prevLists.map((list) => (list.id === updatedList.id ? updatedList : list))
        )
        setMyLists((prevLists) =>
            prevLists.map((list) => (list.id === updatedList.id ? updatedList : list))
        )
        return updatedList
    } catch (err) {
        throw err
    }
}


export const setListLocation = (list) => {
    const searchQuery = list?.search_params
    // Remove any leading '?' from searchQuery to avoid double question marks
    const cleanSearch = searchQuery?.startsWith('?') ? searchQuery.slice(1) : searchQuery
    const url = new URL(window.location.origin + window.location.pathname + (cleanSearch ? '?' + cleanSearch : ''))
    if (list?.slug) {
        url.searchParams.set('list', list.slug)
    }
    // Avoid double question marks
    const newURL = url.toString()
    window.history.pushState({ path: newURL }, '', newURL)
    window.location.reload()
}

export const listsParamsMatch = (searchCur, searchList) => {
    const searchParams = new URLSearchParams(searchCur)
    const listParams = new URLSearchParams(searchList)

    let mismatches = []
    const isMatch = [...searchParams].every(([key, value]) => {
        if (key === 'list') return true // Ignore 'list' param
        const listValue = listParams.get(key)
        if (listValue !== value) {
            mismatches.push({ key, searchValue: value, listValue })
            return false
        }
        return true
    })

    //if (!isMatch) console.log('DEBUG: ListOptions params mismatch:', mismatches)
    return isMatch
}

// Fetch a list by ID
export const getList = async (listId) => {
    try {
        const endpoint = `${apiUrl}/lists/${listId}/`
        const response = await axios.get(endpoint, {
            headers: { 'Content-Type': 'application/json' }
        })
        return response.data
    } catch (error) {
        console.error('ERROR: fetchList', error)
        throw error
    }
}

// Collect all exported async functions except the hook itself
const exportedAsyncFns = {
    getPartnerLists,
    getPartnerDetails,
    createList,
    saveList,
    setListLocation,
    listsParamsMatch,
    getList,
    getAllLists
}

// --- Hook using the above methods and providing loading/error state ---
const useListUtils = (shouldLoad = true) => {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    const wrappedFns = {}
    Object.entries(exportedAsyncFns).forEach(([fnName, fn]) => {
        if (typeof fn === 'function') {
            wrappedFns[fnName] = (...args) => {
                setLoading(true)
                setError(null)
                try {
                    const result = fn(...args)
                    // If result is a Promise, handle async
                    if (result && typeof result.then === 'function') {
                        return result
                            .then((res) => res)
                            .catch((err) => {
                                setError(err)
                            })
                            .finally(() => setLoading(false))
                    } else {
                        // Sync function
                        setLoading(false)
                        return result
                    }
                } catch (err) {
                    setError(err)
                    setLoading(false)
                }
            }
        }
    })

    return {
        ...wrappedFns,
        loading,
        error
    }
}

export default useListUtils